import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CardHeader,
  Dialog,
  DialogContent,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  Link,
  Menu,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import CloseIcon from "@mui/icons-material/Close";
import LinkIcon from "@mui/icons-material/Link";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import SearchIcon from "@mui/icons-material/Search";
import FilterBar from "../../../../components/FilterNSearchBar/FilterNSearchBar";
import { upsertItem } from "../../hooks/useTrustCenterAdmin";
import {
  CARD_HEADER_SX,
  FILTER_BAR_WRAP_SX,
  countLabel,
  includesQuery,
} from "../../../Organization/CodeSecurity/codeSecurityShared";
import { DialogHeader, dialogPaperProps } from "../../components/DialogParts";
import { groupByCategory } from "../../data/resources";
import FrameworkLogo from "../../components/FrameworkLogo";
import AddResourceDialog from "../components/AddResourceDialog";
import TruncatedText from "../../components/TruncatedText";
import RefreshButton from "../../components/RefreshButton";
import { FRAMEWORK_STATUSES } from "../../data/updates";
import usePresentFilter from "../../hooks/usePresentFilter";

const FILTERS = {
  Shown: (f) => f.isVisible,
  Hidden: (f) => !f.isVisible,
  Compliant: (f) => f.status === "compliant",
  "In progress": (f) => f.status === "in_progress",
};

// Resources a buyer can be sent to. Private files never appear on the page.
// Stands in for the "Reports" category when the customer has none yet; the
// admin page creates it before saving the report (see addReports).
export const NEW_REPORTS_CATEGORY = { id: "new-reports", name: "Reports" };

const linkableResources = (resources) =>
  (resources || []).filter((r) => r.visibility !== "private");

// Pick a resource to attach, grouped the way the Resources tab groups them.
const AttachReportDialog = ({
  open,
  frameworkName,
  categories,
  resources,
  linkedIds,
  onPick,
  onClose,
}) => {
  const theme = useTheme();
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (open) setSearch("");
  }, [open]);

  const groups = useMemo(() => {
    const matched = resources.filter((resource) =>
      includesQuery(search, resource.name, resource.description)
    );
    const grouped = groupByCategory(categories, matched);
    const placed = new Set(
      grouped.flatMap((group) => group.resources.map((resource) => resource.id))
    );
    const loose = matched.filter((resource) => !placed.has(resource.id));
    return loose.length
      ? [...grouped, { id: "other", name: "Other", resources: loose }]
      : grouped;
  }, [categories, resources, search]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={dialogPaperProps}
    >
      <DialogHeader
        title="Attach report"
        subtitle={
          frameworkName
            ? `Resources on your Trust Center, grouped by category. The one you choose is linked to ${frameworkName}.`
            : ""
        }
        onClose={onClose}
      />
      <DialogContent dividers>
        <TextField
          size="small"
          fullWidth
          autoFocus
          placeholder="Search resources"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <Box
          role="list"
          aria-label="Resources"
          sx={{
            mt: 1.5,
            maxHeight: 360,
            overflowY: "auto",
            borderRadius: "8px",
            border: `1px solid ${theme.customColors.border.main}`,
          }}
        >
          {groups.length === 0 && (
            <Typography
              variant="body2"
              sx={{ p: 2, color: theme.customColors.text.secondary }}
            >
              {search
                ? "No resources match."
                : "No resources to attach yet. Add one on the Resources tab."}
            </Typography>
          )}
          {groups.map((group) => (
            <Box key={group.id} component="section" aria-label={group.name}>
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  px: 1.5,
                  pt: 1.25,
                  pb: 0.5,
                  fontWeight: 600,
                  color: theme.customColors.text.strong,
                  backgroundColor: theme.customColors.background.cardHeader,
                }}
              >
                {group.name}
              </Typography>
              {group.resources.map((resource) => {
                const linked = linkedIds.has(resource.id);
                return (
                  <Stack
                    role="listitem"
                    key={resource.id}
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    sx={{
                      px: 1.5,
                      py: 0.25,
                      borderTop: `1px solid ${theme.customColors.border.main}`,
                    }}
                  >
                    <Button
                      fullWidth
                      disabled={linked}
                      onClick={() => onPick(resource.id)}
                      sx={{
                        justifyContent: "space-between",
                        textTransform: "none",
                        color: theme.customColors.text.strong,
                        fontWeight: 500,
                        px: 0.5,
                      }}
                    >
                      <TruncatedText value={resource.name} />
                      {linked && (
                        <Typography
                          variant="caption"
                          sx={{
                            color: theme.customColors.text.secondary,
                            ml: 1,
                          }}
                        >
                          Attached
                        </Typography>
                      )}
                    </Button>
                  </Stack>
                );
              })}
            </Box>
          ))}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

// One framework: logo, name, its fixed description, status, and the reports
// a buyer can open. A report is a resource from the Resources tab.
const FrameworkCard = ({
  framework,
  resources,
  onChange,
  onOpenResource,
  onAttach,
  onUpload,
}) => {
  const theme = useTheme();
  // The link icon's menu: upload a new report or attach a resource.
  const [menuAnchor, setMenuAnchor] = useState(null);
  const compliant = framework.status === "compliant";
  const linked = (framework.resourceIds || [])
    .map((id) => resources.find((r) => r.id === id))
    .filter(Boolean);

  const setReports = (resourceIds) =>
    onChange({ id: framework.id, resourceIds });

  return (
    <Card
      component="article"
      aria-label={framework.name}
      elevation={0}
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        borderRadius: "12px",
        border: `1px solid ${theme.customColors.border.main}`,
        opacity: framework.isVisible ? 1 : 0.75,
      }}
    >
      <CardContent sx={{ p: 2.5, flex: 1 }}>
        {/* Logo, name and status; the link icon adds a report. */}
        <Stack direction="row" spacing={1.5} alignItems="center">
          <FrameworkLogo framework={framework} size={44} alt="" />
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography sx={{ fontWeight: 600 }} noWrap>
              {framework.name}
            </Typography>
            <Chip
              size="small"
              label={compliant ? "Compliant" : "In progress"}
              sx={{
                mt: 0.5,
                height: 22,
                fontSize: 12,
                fontWeight: 600,
                color: compliant
                  ? theme.customColors.status.success.text
                  : theme.customColors.text.secondary,
                backgroundColor: compliant
                  ? theme.customColors.status.success.bg
                  : theme.customColors.background.light,
              }}
            />
          </Box>
          <Tooltip title="Link a report" arrow>
            <IconButton
              size="small"
              aria-label={`Link a report to ${framework.name}`}
              aria-haspopup="menu"
              onClick={(e) => setMenuAnchor(e.currentTarget)}
              sx={{
                alignSelf: "flex-start",
                border: `1px solid ${theme.customColors.border.main}`,
                borderRadius: "8px",
              }}
            >
              <LinkIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
          <Menu
            anchorEl={menuAnchor}
            open={!!menuAnchor}
            onClose={() => setMenuAnchor(null)}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
          >
            <MenuItem
              onClick={() => {
                setMenuAnchor(null);
                onUpload();
              }}
            >
              <UploadFileOutlinedIcon sx={{ fontSize: 18, mr: 1.25 }} />
              Upload a new report
            </MenuItem>
            {resources.length > 0 && (
              <MenuItem
                onClick={() => {
                  setMenuAnchor(null);
                  onAttach();
                }}
              >
                <AttachFileIcon sx={{ fontSize: 18, mr: 1.25 }} />
                Attach from Resources
              </MenuItem>
            )}
          </Menu>
        </Stack>
        {framework.description && (
          <Typography
            variant="body2"
            title={framework.description}
            sx={{
              mt: 1.5,
              color: theme.customColors.text.secondary,
              lineHeight: 1.6,
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {framework.description}
          </Typography>
        )}

        {/* Reports: one quiet row each; the remove button shows on hover. */}
        <Box sx={{ mt: 2 }}>
          <Typography
            variant="caption"
            sx={{
              display: "block",
              mb: 0.75,
              fontWeight: 600,
              color: theme.customColors.text.secondary,
            }}
          >
            Reports{linked.length > 0 ? ` (${linked.length})` : ""}
          </Typography>
          {linked.length === 0 ? (
            <Typography
              variant="body2"
              sx={{ color: theme.customColors.text.secondary }}
            >
              No report linked yet. Use the link icon to add one.
            </Typography>
          ) : (
            <Stack spacing={0.75}>
              {linked.map((resource) => (
                <Stack
                  key={resource.id}
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  sx={{
                    pl: 1.25,
                    pr: 0.5,
                    py: 0.25,
                    minHeight: 36,
                    borderRadius: "8px",
                    border: `1px solid ${theme.customColors.border.main}`,
                    backgroundColor: theme.customColors.background.main,
                    "& .remove-report": { opacity: { xs: 1, md: 0 } },
                    "&:hover .remove-report, & .remove-report:focus-visible": {
                      opacity: 1,
                    },
                  }}
                >
                  <DescriptionOutlinedIcon
                    sx={{
                      fontSize: 17,
                      color: theme.customColors.icons.secondary,
                    }}
                  />
                  <Link
                    component="button"
                    type="button"
                    variant="body2"
                    underline="hover"
                    onClick={() => onOpenResource?.(resource.id)}
                    aria-label={`Open ${resource.name} on Resources`}
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      textAlign: "left",
                      color: theme.customColors.text.strong,
                    }}
                  >
                    <TruncatedText value={resource.name} />
                  </Link>
                  <Tooltip title="Unlink" arrow>
                    <IconButton
                      className="remove-report"
                      size="small"
                      aria-label={`Remove report ${resource.name} from ${framework.name}`}
                      onClick={() =>
                        setReports(
                          (framework.resourceIds || []).filter(
                            (id) => id !== resource.id
                          )
                        )
                      }
                    >
                      <CloseIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                </Stack>
              ))}
            </Stack>
          )}
        </Box>
      </CardContent>
      <Divider sx={{ borderColor: theme.customColors.border.main }} />
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ px: 2.5, py: 1.25, flexWrap: "wrap", rowGap: 1 }}
      >
        <Select
          size="small"
          value={compliant ? "compliant" : "in_progress"}
          onChange={(e) =>
            onChange({ id: framework.id, status: e.target.value })
          }
          SelectDisplayProps={{ "aria-label": `Status of ${framework.name}` }}
          sx={{ minWidth: 140, fontSize: 14 }}
        >
          {Object.entries(FRAMEWORK_STATUSES).map(([value, label]) => (
            <MenuItem key={value} value={value}>
              {label}
            </MenuItem>
          ))}
        </Select>
        <FormControlLabel
          labelPlacement="start"
          sx={{ mr: 0 }}
          label={
            <Typography variant="body2">
              {framework.isVisible ? "Shown on page" : "Hidden"}
            </Typography>
          }
          control={
            <Switch
              checked={!!framework.isVisible}
              onChange={(e) =>
                onChange({ id: framework.id, isVisible: e.target.checked })
              }
              inputProps={{
                "aria-label": `Show ${framework.name} on page`,
              }}
            />
          }
        />
      </Stack>
    </Card>
  );
};

// Compliance tab: frameworks come from the customer's Regodit subscription.
// The admin picks which to show, sets the status, and attaches resources
// from the Resources tab as reports.
const ComplianceTab = ({
  frameworks,
  resources = [],
  categories = [],
  onChange,
  onOpenResource,
  onAddReports,
}) => {
  const theme = useTheme();
  const [search, setSearch] = useState("");
  const {
    filter,
    setFilter,
    options: filterOptions,
  } = usePresentFilter(frameworks, FILTERS);
  const choices = useMemo(() => linkableResources(resources), [resources]);
  const [picking, setPicking] = useState(null);
  // The framework a new report is being uploaded for.
  const [uploadingFor, setUploadingFor] = useState(null);
  const existingPolicyIds = useMemo(
    () => new Set(resources.filter((r) => r.policyId).map((r) => r.policyId)),
    [resources]
  );
  // A category named like "reports" first, so the upload lands there. With
  // no categories yet, a new "Reports" category is made on save.
  const reportCategories = useMemo(() => {
    if (categories.length === 0) return [NEW_REPORTS_CATEGORY];
    const isReports = (c) => /report/i.test(c.name);
    return [
      ...categories.filter(isReports),
      ...categories.filter((c) => !isReports(c)),
    ];
  }, [categories]);

  const shown = useMemo(
    () =>
      frameworks.filter(
        (f) =>
          (!filter || FILTERS[filter](f)) &&
          includesQuery(search, f.name, f.description)
      ),
    [frameworks, search, filter]
  );

  const setFramework = (framework) =>
    onChange({ frameworks: upsertItem(frameworks, framework) });

  return (
    <Card
      sx={{
        borderRadius: "12px",
        border: `1px solid ${theme.customColors.border.main}`,
        boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
      }}
    >
      <CardHeader
        sx={CARD_HEADER_SX}
        title={
          <Typography sx={{ fontSize: 16, fontWeight: 600 }}>
            Compliance
          </Typography>
        }
        subheader={
          <Typography
            variant="body2"
            sx={{ color: theme.customColors.text.secondary, mt: 0.25 }}
          >
            Frameworks from your Regodit subscription. Choose which to show, set
            the status, and upload or attach a report.
          </Typography>
        }
        action={<RefreshButton label="Compliance" />}
      />
      <CardContent sx={{ p: 0, "&:last-child": { pb: 0 } }}>
        <Box sx={FILTER_BAR_WRAP_SX}>
          <FilterBar
            searchQuery={search}
            onSearchChange={(event) => setSearch(event.target.value)}
            filters={filterOptions}
            selectedFilter={filter}
            onFilterChange={setFilter}
            onClearFilters={() => {
              setSearch("");
              setFilter(null);
            }}
            countLabel={countLabel(shown.length, "framework")}
          />
        </Box>
        <Divider sx={{ borderColor: theme.customColors.border.main }} />
        <Box sx={{ p: 2.5 }}>
          {shown.length === 0 ? (
            <Typography
              variant="body2"
              sx={{
                color: theme.customColors.text.secondary,
                p: 1,
                textAlign: "center",
              }}
            >
              No frameworks match.
            </Typography>
          ) : (
            <Grid container spacing={2}>
              {shown.map((framework) => (
                <Grid item xs={12} md={6} key={framework.id}>
                  <FrameworkCard
                    framework={framework}
                    resources={choices}
                    onChange={setFramework}
                    onOpenResource={onOpenResource}
                    onAttach={() => setPicking(framework)}
                    onUpload={() => setUploadingFor(framework)}
                  />
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
      </CardContent>
      <AddResourceDialog
        open={!!uploadingFor}
        title={`Upload report for ${uploadingFor?.name || ""}`}
        initialSource="document"
        categories={reportCategories}
        existingPolicyIds={existingPolicyIds}
        onClose={() => setUploadingFor(null)}
        onAdd={(newResources) => onAddReports(uploadingFor.id, newResources)}
      />
      <AttachReportDialog
        open={!!picking}
        frameworkName={picking?.name || ""}
        categories={categories}
        resources={choices}
        linkedIds={new Set(picking?.resourceIds || [])}
        onClose={() => setPicking(null)}
        onPick={(id) => {
          setFramework({
            id: picking.id,
            resourceIds: [...(picking.resourceIds || []), Number(id)],
          });
          setPicking(null);
        }}
      />
    </Card>
  );
};

export default ComplianceTab;
